<script setup>
import axios from "axios";
import { ref, onMounted, computed, watch } from "vue";
import { useStore } from "vuex";

const { user: userProp } = defineProps(["user"]);
const emit = defineEmits(["setVisibility"]);

const store = useStore();

// Use computed user from store to ensure reactivity
const user = computed(() => store.state.auth.user);

const isCheckedPerm = ref(false);

const checkDisclaimerValidity = () => {
  const disclaimer = user.value?.disclaimer;

  if (!disclaimer || !disclaimer.accepted_at) {
    emit("setVisibility", true);
    return;
  }

  const { accepted_at, is_accepted_temporarily } = disclaimer;

  const threeDays = 259200000; // 3 days in milliseconds
  const oneYear = 31557600000; // 1 year in milliseconds

  // accepted_at is already in seconds, so multiply by 1000 to get milliseconds
  const acceptedAtMs = typeof accepted_at === 'number' ? accepted_at * 1000 : Date.parse(accepted_at);
  const diff = Date.now() - acceptedAtMs;

  // Show disclaimer if more than a year has passed, or if more than 3 days and not permanently accepted
  if (diff > oneYear || (diff > threeDays && is_accepted_temporarily)) {
    emit("setVisibility", true);
  } else {
    emit("setVisibility", false);
  }
};

const handleSave = async () => {
  const apiUrl = import.meta.env.VITE_APP_API_BASE_URL;

  try {
    await axios.post(`${apiUrl}/api/user/accept-disclaimer`, {
      is_accepted_temporarily: isCheckedPerm.value,
    });

    // Update the store with the new disclaimer state
    store.commit('auth/updateDisclaimer', {
      is_accepted_temporarily: isCheckedPerm.value,
    });

    emit("setVisibility", false);
  } catch (error) {
    console.error('Error saving disclaimer:', error);
  }
};

// Watch for changes in user disclaimer state
watch(
  () => user.value?.disclaimer,
  () => {
    checkDisclaimerValidity();
  },
  { deep: true, immediate: false }
);

onMounted(() => {
  checkDisclaimerValidity();
});
</script>
<template>
  <div class="disclaimer">
    <p class="title">{{ $t("chat.disclaimer_1") }}</p>
    <p class="description">{{ $t("chat.disclaimer_2") }}</p>
    <div class="input">
      <input class="checkbox" v-model="isCheckedPerm" id="checkbox" type="checkbox" />
      <label class="label" for="checkbox">{{ $t("chat.disclaimer_3") }}</label>
    </div>

    <VBtn @click="handleSave" type="button" class="accept-button" variant="flat">
      {{ $t("chat.disclaimer_4") }}
    </VBtn>
  </div>
</template>
<style scoped lang="scss">
.disclaimer {
  padding: 55px 30px;
  .title {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 20px;
  }
  .description {
    margin-bottom: 20px;
    font-size: 11px;
  }

  .input {
    display: flex;
    align-items: center;
    margin-bottom: 30px;
    gap: 10px;

    .label {
      font-size: 11px;
      opacity: 1;
      font-weight: 500;
      user-select: none;
      &:hover {
        cursor: pointer;
        opacity: 0.9;
      }
    }
    .checkbox {
      cursor: pointer;
    }
  }

  .accept-button {
    font-size: 1.125rem;
    color: white !important;
    background-color: var(--cambridge-blue);
    text-transform: none;
    border-radius: 0;
    padding: 12px 16px;
    width: 100%;
    min-height: 45px;
    height: auto;
    margin-top: 35px;
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
    overflow-wrap: break-word;
  }
}
@media (max-width: 1050px) {
  .disclaimer {
    .accept-button {
      margin-top: 0;
    }
  }
}
@media (max-height: 850px) {
  .disclaimer {
    padding: 10px 10px;
    .title {
      font-size: 12px;
      margin-bottom: 10px;
    }
    .description {
      margin-bottom: 10px;
    }
    .input {
      margin-bottom: 10px;
    }
    .accept-button {
      margin-top: 10px;
    }
  }
}
</style>
